import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import Script from 'next/script';
import { useMemo } from 'react';
import type { SwitchableContentSelectorContent } from '@/components/SwitchableContentSelector/SwitchableContentSelector';
import type {
  CardContent,
  CardGroupContent,
} from '@/contentful/contentful-exports';
import type { SectionConstraintsCF } from '@/types';
import type { FAQInfoAccordionContent } from '../../FAQInfoAccordion/FAQInfoAccordion';
import type { EnhancedSectionContent } from '../../GenericComponent/GenericComponent';
import type { EnhancedSectionType } from '../../Section/Section';
import type {
  AccordionContent,
  GlobalFAQContent,
} from '../../TopLevelComponents/GlobalFAQ/GlobalFAQ';

interface Props {
  sections: EnhancedSectionType[];
}

const isGlobalFAQ = (
  content: EnhancedSectionContent
): content is GlobalFAQContent => {
  return content?.sys?.contentType?.sys?.id === 'globalFaq';
};
const isFAQInfoAccordion = (
  content: EnhancedSectionContent
): content is FAQInfoAccordionContent => {
  return content?.sys?.contentType?.sys?.id === 'faqinfoAccordion';
};

const isSectionConstraints = (
  // biome-ignore lint/suspicious/noExplicitAny: SectionType doesn't include SectionStateConstraintsList
  content: any
): content is SectionConstraintsCF => {
  return content?.sys?.contentType?.sys?.id === 'sectionConstraints';
};

const isSwitchableContentSelector = (
  content: EnhancedSectionContent
): content is SwitchableContentSelectorContent => {
  return content.sys.contentType.sys.id === 'switchableContentSelector';
};

const getSectionContent = (
  sections: EnhancedSectionType[]
): EnhancedSectionContent[] => {
  const content: EnhancedSectionContent[] = [];

  sections.forEach((section) => {
    const entries = section.fields?.content || [];

    entries.forEach((entry) => {
      if (isSectionConstraints(entry) && entry.fields.default) {
        content.push(...(entry.fields.default as EnhancedSectionContent[]));
      } else if (isSwitchableContentSelector(entry)) {
        const nestedSections = getSectionContent(
          entry.fields.sectionToggleSections
        );

        content.push(...nestedSections);
      } else {
        content.push(entry);
      }
    });
  });

  return content;
};

const getGlobalFAQs = (
  content: EnhancedSectionContent[]
): GlobalFAQContent[] => {
  return content.filter(isGlobalFAQ);
};
const getFAQInfoAccordions = (content: EnhancedSectionContent[]) => {
  return content.filter(isFAQInfoAccordion);
};

const getGlobalFAQsData = (globalFAQs: GlobalFAQContent[]) => {
  const faqs: AccordionContent[] = [];

  globalFAQs.forEach((globalFAQ) => {
    globalFAQ.fields.faqs.forEach((faq) => {
      faqs.push(faq);
    });
  });

  return faqs.map((faq) => ({
    question: faq.fields.summary,
    answer: documentToPlainTextString(faq.fields.details),
  }));
};

const getFAQInfoAccordionsData = (
  faqinfoAccordions: FAQInfoAccordionContent[]
) => {
  const faqs: CardGroupContent[] = [];

  faqinfoAccordions.forEach((faqinfoAccordion) => {
    faqinfoAccordion.fields.expandableSections.forEach((faq) => {
      faqs.push(faq);
    });
  });

  const getAnswer = (cards: CardContent[]) => {
    const answers: string[] = [];

    cards.forEach((card) => {
      if (card.fields.richDescription) {
        answers.push(documentToPlainTextString(card.fields.richDescription));
      } else if (card.fields.description) {
        answers.push(card.fields.description);
      }
    });

    return answers.join('\n');
  };

  return faqs.map((faq) => ({
    question: faq.fields.title,
    answer: getAnswer(faq.fields.cards),
  }));
};

const getFAQData = (sections: EnhancedSectionType[]) => {
  const sectionContent = getSectionContent(sections);

  const globalFAQs = getGlobalFAQs(sectionContent);
  const faqinfoAccordions = getFAQInfoAccordions(sectionContent);

  const globalFAQsData = getGlobalFAQsData(globalFAQs);
  const faqinfoAccordionsData = getFAQInfoAccordionsData(faqinfoAccordions);

  return [...globalFAQsData, ...faqinfoAccordionsData];
};

export default function FAQPageSchema({ sections }: Props) {
  const faqs = useMemo(() => getFAQData(sections), [sections]);
  const schema = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      }),
    [faqs]
  );

  if (faqs.length === 0) return null;
  return (
    <Script type='application/ld+json' strategy='beforeInteractive'>
      {`${schema}`}
    </Script>
  );
}
